/**
 * Collects the currently-available Mail and Files quick-access shortcuts for
 * the app bar, gated by the same signals the rest of the app uses:
 *
 * - PanLuma Inbox / Files  → the email / files module being available to the
 *   user. Email and Files are "pinned" utility modules with no per-feature
 *   on/off toggle; their availability is gated by sysadmin platform-availability
 *   + admin module-ceiling + role — exactly the set the module picker exposes as
 *   `pinnedModules`. We mirror that here so an icon shows iff the module is
 *   reachable from the picker (one consistent gate, not a separate one).
 * - Gmail / Google Drive   → a live Google integration.
 * - Outlook / OneDrive     → a live Microsoft integration.
 *
 * Returns the present items split by group plus the running total, ready for
 * {@link computeShortcutLayout} to decide direct-icons vs. grouped-dropdowns.
 */

import { useMemo } from 'react';
import type { ReactNode } from 'react';
import { Inbox, Files } from 'lucide-react';
import { GmailIcon, GoogleDriveIcon, OutlookIcon, OneDriveIcon } from '../../../shared/components/icons';
import { useModule } from '../../../shared/contexts/ModuleContext';
import { useGoogleIntegration } from '../../../shared/users/hooks/useGoogleIntegration';
import { useMicrosoftIntegration } from '../../../shared/users/hooks/useMicrosoftIntegration';
import { useUnreadCount } from '../../../modules/email/hooks/useInbox';
import type { ShortcutItem } from './computeShortcutLayout';

export interface MailFilesShortcuts {
  mail: ShortcutItem<ReactNode>[];
  files: ShortcutItem<ReactNode>[];
}

const ICON_CLASS = 'h-4 w-4';

export function useMailFilesShortcuts(): MailFilesShortcuts {
  const { pinnedModules } = useModule();
  const { data: googleStatus } = useGoogleIntegration();
  const isGoogleConnected = googleStatus?.data?.google_connected === true;
  const { data: microsoftStatus } = useMicrosoftIntegration();
  const isMicrosoftConnected = microsoftStatus?.data?.connected === true;

  // Email/Files are pinned utility modules — available iff present in the
  // picker's accessible `pinnedModules` set (role + platform + ceiling + the
  // per-tenant/user enable toggle, which pinned filtering now respects).
  const isEmailEnabled = pinnedModules.some((m) => m.id === 'email');
  const isFilesEnabled = pinnedModules.some((m) => m.id === 'files');

  // PanLuma Inbox unread badge — only queried when the inbox is actually shown.
  const { data: unread } = useUnreadCount({ enabled: isEmailEnabled });
  const inboxUnread = unread?.count ?? 0;

  return useMemo(() => {
    const mail: ShortcutItem<ReactNode>[] = [];
    const files: ShortcutItem<ReactNode>[] = [];

    if (isEmailEnabled) {
      mail.push({ key: 'inbox', group: 'mail', labelKey: 'nav.mail.inbox', path: '/inbox', icon: <Inbox className={ICON_CLASS} />, unreadCount: inboxUnread });
    }
    if (isGoogleConnected) {
      mail.push({ key: 'gmail', group: 'mail', labelKey: 'nav.mail.gmail', path: '/gmail', icon: <GmailIcon className={ICON_CLASS} /> });
    }
    if (isMicrosoftConnected) {
      mail.push({ key: 'outlook', group: 'mail', labelKey: 'nav.mail.outlook', path: '/outlook', icon: <OutlookIcon className={ICON_CLASS} /> });
    }

    if (isFilesEnabled) {
      files.push({ key: 'files', group: 'files', labelKey: 'nav.fileManagement.panlumaFiles', path: '/files', icon: <Files className={ICON_CLASS} /> });
    }
    if (isGoogleConnected) {
      files.push({ key: 'drive', group: 'files', labelKey: 'nav.fileManagement.googleDrive', path: '/drive', icon: <GoogleDriveIcon className={ICON_CLASS} /> });
    }
    if (isMicrosoftConnected) {
      files.push({ key: 'onedrive', group: 'files', labelKey: 'nav.fileManagement.oneDrive', path: '/onedrive', icon: <OneDriveIcon className={ICON_CLASS} /> });
    }

    return { mail, files };
  }, [isEmailEnabled, isFilesEnabled, isGoogleConnected, isMicrosoftConnected, inboxUnread]);
}
